import { Box, Link, styled } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { BasicButton } from '../../atoms';
import { ReactComponent as BrokenPageIcon } from '../../../assets/icons/brokenPage.svg';
import { ReactComponent as BrokenPageBgIcon } from '../../../assets/icons/brokenPageBg.svg';
import { useAppSelector } from '../../../common/hooks';

interface FailedToLoadContentProps {
    onRetry: () => void;
}

const Container = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    width: '100%',
    height: '100%',
    minHeight: '60vh'
});

const IconWrapper = styled(Box)({
    position: 'relative',
    width: 100,
    height: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
});

const StyledBrokenPageBg = styled(BrokenPageBgIcon)({
    width: 100,
    height: 100
});

const StyledBrokenPageIcon = styled(BrokenPageIcon)({
    position: 'absolute',
    width: 68,
    height: 68
});

const TextContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    textAlign: 'center'
});

const Title = styled('p')(({ theme }) => ({
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: '24px',
    color: theme.palette.text.primary,
    margin: 0
}));

const Subtitle = styled('p')(({ theme }) => ({
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '20px',
    color: theme.palette.text.primary,
    margin: 0,
    '& strong': {
        fontWeight: 600,
        textDecoration: 'underline',
        color: theme.palette.semantic.text.brand,
        cursor: 'pointer'
    }
}));

export const FailedToLoadContent = ({ onRetry }: FailedToLoadContentProps) => {
    const { t } = useTranslation();

    const supportEmail = useAppSelector(
        (state) => state.global?.globalConfig?.supportEmail || t('APPLICATION_SUPPORT_EMAIL')
    );

    return (
        <Container>
            <IconWrapper>
                <StyledBrokenPageBg />
                <StyledBrokenPageIcon />
            </IconWrapper>
            <TextContainer>
                <Title>{t('FAILED_TO_LOAD_DATA')}</Title>
                <Subtitle>
                    <Trans
                        i18nKey="RETRY_CONTACT_US"
                        components={{
                            strong: (
                                <Link
                                    href={`mailto:${supportEmail}`}
                                    underline="always"
                                    sx={{
                                        fontWeight: 600,
                                        color: (theme) => theme.palette.semantic.text.brand,
                                        cursor: 'pointer'
                                    }}
                                />
                            )
                        }}
                    />
                </Subtitle>
            </TextContainer>
            <BasicButton variant="contained" onClick={onRetry} sx={{ width: 179, height: 40 }}>
                {t('TEXT_ERROR_RETRY')}
            </BasicButton>
        </Container>
    );
};

export default FailedToLoadContent;
